import { ArrowLeftOutlined } from '@ant-design/icons'
import { GraviButton, Horizontal, MaterialIcon, Texto } from '@gravitate-js/excalibrr'
import React, { useCallback, useEffect } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { Switch, Tooltip } from 'antd'

interface MatchDetailNavigationHeaderProps {
  isDetailsView: boolean
  setIsDetailsView: (value: boolean) => void
  isSummaryView: boolean
  setIsSummaryView: (value: boolean) => void
  isManageMode: boolean
  setIsManageMode: (value: boolean) => void
  matchStatus?: string
}

export function MatchDetailNavigationHeader({
  isDetailsView,
  setIsDetailsView,
  isSummaryView,
  setIsSummaryView,
  isManageMode,
  setIsManageMode,
  matchStatus,
}: MatchDetailNavigationHeaderProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { id } = useParams()
  const { displayedRows = [], currentIndex = -1, filterContext = {} } = location.state || {}

  // Calculate the display index (1-based) and handle invalid currentIndex
  const displayIndex = currentIndex >= 0 ? currentIndex + 1 : 1
  const validCurrentIndex = currentIndex >= 0 ? currentIndex : 0

  // Determine if we can navigate to prev/next
  const isFirstMatch = validCurrentIndex <= 0
  const isLastMatch = validCurrentIndex >= displayedRows.length - 1

  // Navigation handlers
  const goToPrevious = useCallback(() => {
    if (isFirstMatch) return
    const prevMatch = displayedRows[validCurrentIndex - 1]
    const prevId = prevMatch.id || 'no-match-id'
    navigate(`/supplier_rec/match_view/${prevId}`, {
      state: {
        displayedRows,
        currentIndex: validCurrentIndex - 1,
        filterContext,
        matchData: prevMatch.id ? undefined : prevMatch,
        noMatchId: !prevMatch.id,
      },
    })
  }, [displayedRows, validCurrentIndex, isFirstMatch, navigate, filterContext])

  const goToNext = useCallback(() => {
    if (isLastMatch) return
    const nextMatch = displayedRows[validCurrentIndex + 1]
    const nextId = nextMatch.id || 'no-match-id'
    navigate(`/supplier_rec/match_view/${nextId}`, {
      state: {
        displayedRows,
        currentIndex: validCurrentIndex + 1,
        filterContext,
        matchData: nextMatch.id ? undefined : nextMatch,
        noMatchId: !nextMatch.id,
      },
    })
  }, [displayedRows, validCurrentIndex, isLastMatch, navigate, filterContext])

  const backToList = () => {
    // Exit manage mode if active before navigating
    if (isManageMode) {
      setIsManageMode(false)
    } else {
      navigate('/supplier_rec/match_view', {
        state: filterContext,
      })
    }
  }

  // Add keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle navigation if we have displayedRows
      if (!displayedRows.length) return

      // Ignore if user is typing in an input
      if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return

      switch (e.key) {
        case 'ArrowLeft':
          // Exit manage mode if active, otherwise go to previous
          if (isManageMode) {
            setIsManageMode(false)
          } else {
            goToPrevious()
          }
          break
        case 'ArrowRight':
          // Exit manage mode if active, otherwise go to next
          if (isManageMode) {
            setIsManageMode(false)
          } else {
            goToNext()
          }
          break
        default:
          break
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [displayedRows.length, goToNext, goToPrevious, isManageMode, setIsManageMode])

  return (
    <Horizontal
      verticalCenter
      justifyContent='space-between'
      className='px-4 py-2'
      bg='elevated'
      border='all'
      borderRadius='6px 6px 0 0'
      style={{ borderBottom: '1px solid var(--border-color)' }}
    >
      <Horizontal verticalCenter gap={8} onClick={backToList} style={{ cursor: 'pointer' }}>
        <ArrowLeftOutlined style={{ fontSize: 16 }} />
        <Texto category='h5' weight='bold'>
          Back to Matches
        </Texto>
      </Horizontal>

      <Horizontal verticalCenter gap={16}>
        {displayedRows.length > 0 && (
          <Horizontal verticalCenter gap={8}>
            <Texto category='h4' weight='bold' style={{ margin: 0 }}>
              Match Navigation
            </Texto>
            <Texto category='h5' style={{ margin: 0, textAlign: 'center' }}>
              {displayIndex} of {displayedRows.length}
            </Texto>
            <MaterialIcon
              icon='chevron_left'
              style={{
                fontSize: 24,
                cursor: isFirstMatch ? 'not-allowed' : 'pointer',
                opacity: isFirstMatch ? 0.5 : 1,
                margin: 0,
              }}
              onClick={goToPrevious}
            />
            <MaterialIcon
              icon='chevron_right'
              style={{
                fontSize: 24,
                cursor: isLastMatch ? 'not-allowed' : 'pointer',
                opacity: isLastMatch ? 0.5 : 1,
                margin: 0,
              }}
              onClick={goToNext}
            />
          </Horizontal>
        )}

        {/* Toggle Controls and Actions */}
        <Horizontal gap={24} verticalCenter>
          <Horizontal gap={8} verticalCenter>
            <Texto>Details Panel</Texto>
            <Switch checked={isDetailsView} onChange={setIsDetailsView} size='small' />
          </Horizontal>
          <Horizontal gap={8} verticalCenter>
            <Texto>Summary View</Texto>
            <Switch checked={isSummaryView} onChange={setIsSummaryView} size='small' />
          </Horizontal>
          <Tooltip
            title={
              matchStatus === 'approved'
                ? 'Cannot manage matches for approved items'
                : 'Search for and link invoices/BOLs to this match'
            }
          >
            <GraviButton
              theme2
              size='small'
              onClick={() => setIsManageMode(true)}
              disabled={matchStatus === 'approved'}
              icon={<MaterialIcon icon='link' className='pt-1' />}
            >
              Manage Match
            </GraviButton>
          </Tooltip>
        </Horizontal>
      </Horizontal>
    </Horizontal>
  )
}
